import { Reference, useMutation } from "@apollo/client";
import { Browser } from "@capacitor/browser";
import { yupResolver } from "@hookform/resolvers/yup";
import { IonCol, IonGrid, IonRow, isPlatform } from "@ionic/react";
import HelloSign from "hellosign-embedded";
import { MouseEvent, useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import * as yup from "yup";

import ConsentFormBlank from "@assets/pdfs/ConsentFormBlank.pdf";
import Button from "@components/buttons/Button";
import FormButtons from "@components/fields/FormButtons";
import Input from "@components/fields/Input";
import RadioGroup from "@components/fields/RadioGroup";
import styles from "@components/forms/ConsentFormWizard.module.css";
import Form from "@components/forms/Form";
import Modal from "@components/Modal";
import IconBase from "@components/text/IconBase";
import useMe from "@hooks/useMe";
import { Opener } from "@hooks/useOpener";
import {
  SignedDocumentCreateDocument,
  SignedDocumentCreateMutation,
  SignedDocumentCreateMutationVariables,
  SignedDocumentFragmentDoc,
  SignedDocumentKindEnum,
  SignedDocumentMediumEnum
} from "@typing/Generated";
import { downloadFile } from "@utils/downloadUtils";

type Props = {
  journeyId: string;
  onFormMaybeCompleted: () => void;
  opener: Opener;
};

type MediumSelectionFormProps = {
  onCancel: () => void;
  onMediaSelection: (medium: SignedDocumentMediumEnum, documentFor?: string | null) => void;
};

type DetailFormProps = {
  documentFor?: string | null;
  onBack: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSuccess: (data?: SignedDocumentCreateData) => Promise<any>;
};

type CompleteUploadProps = {
  onBack: () => void;
  onSuccess: () => void;
};

enum WizardStep {
  MEDIUM_SELECTION = "MEDIA_SELECTION",
  SIGNATURE_SETTINGS = "SIGNATURE_SETTINGS"
}

enum MediumChoice {
  EMAIL = "EMAIL",
  EMBED_PARENT = "EMBED_PARENT",
  EMBED_POWER_OF_ATTORNEY = "EMBED_POWER_OF_ATTORNEY",
  EMBED_YOURSELF = "EMBED_YOURSELF",
  UPLOAD = "UPLOAD"
}

type MediumSelectionFormData = {
  medium: MediumChoice | null;
};

type EmailFormData = {
  documentFor: string | null;
  recipientEmailAddress: string | null;
  recipientName: string | null;
};

type EmbedFormData = {
  documentFor: string | null;
};

type SignedDocumentCreateData = {
  documentFor: string | null;
  recipientEmailAddress?: string | null;
  recipientName?: string | null;
};

const MediumSelectionForm = ({ onCancel, onMediaSelection }: MediumSelectionFormProps) => {
  const intl = useIntl();
  const me = useMe();

  const schema: yup.ObjectSchema<MediumSelectionFormData> = yup
    .object()
    .shape({
      medium: yup
        .string()
        .oneOf(Object.values(MediumChoice))
        .required(intl.formatMessage({ id: "errors.consentForm.medium.blank" }))
    })
    .required();

  const form = useForm<MediumSelectionFormData>({
    defaultValues: { medium: null },
    mode: "onChange",
    resolver: yupResolver(schema)
  });

  const handleCancel = useCallback(() => {
    form.reset();
    onCancel();
  }, [form, onCancel]);

  const handleSubmit = useCallback(() => {
    const value = form.getValues().medium;
    if (value === MediumChoice.EMAIL) {
      onMediaSelection(SignedDocumentMediumEnum.EMAIL);
    } else if (value === MediumChoice.UPLOAD) {
      onMediaSelection(SignedDocumentMediumEnum.UPLOAD);
    } else {
      onMediaSelection(SignedDocumentMediumEnum.EMBED, me?.fullName);
    }
    form.reset();
  }, [form, me, onMediaSelection]);

  const mediumOptionsEmbed = useMemo(() => {
    const options = [MediumChoice.EMBED_YOURSELF, MediumChoice.EMBED_PARENT, MediumChoice.EMBED_POWER_OF_ATTORNEY];
    return options.map(option => ({
      label: intl.formatMessage({ id: `pages.consentFormWizard.medium.embed.${option}` }),
      value: option
    }));
  }, [intl]);

  const mediumOptionsOther = useMemo(() => {
    const options = [MediumChoice.UPLOAD, MediumChoice.EMAIL];
    return options.map(option => ({
      label: intl.formatMessage({ id: `pages.consentFormWizard.medium.other.${option}` }),
      value: option
    }));
  }, [intl]);

  return (
    <Form hookForm={form} onSubmit={form.handleSubmit(handleSubmit)}>
      <p>
        <FormattedMessage id="pages.consentFormWizard.explanation1" />
      </p>
      <p>
        <FormattedMessage id="pages.consentFormWizard.explanation2" />
      </p>
      <RadioGroup<MediumSelectionFormData>
        control={form.control}
        error={form.formState.errors.medium}
        name="medium"
        options={mediumOptionsEmbed}
      />
      <p>
        <FormattedMessage id="pages.consentFormWizard.explanation3" />
      </p>
      <RadioGroup<MediumSelectionFormData>
        control={form.control}
        error={form.formState.errors.medium}
        name="medium"
        options={mediumOptionsOther}
      />
      <FormButtons hookForm={form} onCancel={handleCancel} saveKey="dictionary.next" />
    </Form>
  );
};

const CompleteEmailForm = ({ onBack, onSuccess }: DetailFormProps) => {
  const intl = useIntl();

  const schema: yup.ObjectSchema<EmailFormData> = yup
    .object()
    .shape({
      documentFor: yup.string().required(intl.formatMessage({ id: "errors.consentForm.documentFor.blank" })),
      recipientEmailAddress: yup
        .string()
        .required(intl.formatMessage({ id: "errors.consentForm.recipientEmailAddress.blank" })),
      recipientName: yup.string().required(intl.formatMessage({ id: "errors.consentForm.recipientName.blank" }))
    })
    .required();

  const defaultValues: EmailFormData = {
    documentFor: "",
    recipientEmailAddress: "",
    recipientName: ""
  };

  const form = useForm<EmailFormData>({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver(schema)
  });

  const onSubmit = useCallback(async () => onSuccess(form.getValues()), [form, onSuccess]);

  return (
    <Form hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      <Input<EmailFormData>
        error={form.formState.errors.documentFor}
        label={intl.formatMessage({ id: "pages.consentFormWizard.email.documentForLabel" })}
        register={form.register("documentFor")}
        required
      />
      <Input<EmailFormData>
        error={form.formState.errors.recipientName}
        label={intl.formatMessage({ id: "pages.consentFormWizard.email.recipientNameLabel" })}
        register={form.register("recipientName")}
        required
      />
      <Input<EmailFormData>
        error={form.formState.errors.recipientEmailAddress}
        label={intl.formatMessage({ id: "pages.consentFormWizard.email.recipientEmailAddressLabel" })}
        register={form.register("recipientEmailAddress")}
        required
      />
      <FormButtons
        cancelKey="dictionary.back"
        hookForm={form}
        onCancel={onBack}
        saveKey="pages.consentFormWizard.sendEmail"
        spinnerWhenSubmitting
      />
    </Form>
  );
};

const CompleteEmbedForm = ({ documentFor, onBack, onSuccess }: DetailFormProps) => {
  const intl = useIntl();

  const schema: yup.ObjectSchema<EmbedFormData> = yup
    .object()
    .shape({
      documentFor: yup.string().required(intl.formatMessage({ id: "errors.consentForm.documentFor.blank" }))
    })
    .required();

  const defaultValues: EmbedFormData = { documentFor: documentFor ?? "" };

  const form = useForm<EmbedFormData>({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver(schema)
  });

  const onSubmit = useCallback(async () => onSuccess(form.getValues()), [form, onSuccess]);

  return (
    <Form hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      <p>
        <FormattedMessage id="pages.consentFormWizard.embed.instructions" />
      </p>
      <Input<EmbedFormData>
        error={form.formState.errors.documentFor}
        label={intl.formatMessage({ id: "pages.consentFormWizard.embed.documentForLabel" })}
        register={form.register("documentFor")}
        required
      />
      <FormButtons
        cancelKey="dictionary.back"
        hookForm={form}
        onCancel={onBack}
        saveKey="dictionary.next"
        skipDirty
        spinnerWhenSubmitting
      />
    </Form>
  );
};

const CompleteUpload = ({ onBack, onSuccess }: CompleteUploadProps) => {
  const handleDownloadBlank = useCallback((event: MouseEvent<HTMLIonButtonElement>) => {
    event.preventDefault();
    downloadFile(ConsentFormBlank, "grayce-consent-form-blank.pdf");
  }, []);

  return (
    <>
      <p>
        <FormattedMessage id="pages.consentFormWizard.upload.instructions" />
      </p>

      <IonGrid className={styles.downloadGrid}>
        <IonRow>
          <IonCol className={styles.downloadFileName}>
            <FormattedMessage id="pages.consentFormWizard.upload.filename" />
          </IonCol>
          <IonCol className={styles.alignRight}>
            <Button fill="clear" onClick={handleDownloadBlank}>
              <IconBase icon="fileDownload" slot="end" />
              <FormattedMessage id="dictionary.download" />
            </Button>
          </IonCol>
        </IonRow>
      </IonGrid>

      <FormButtons
        cancelKey="dictionary.back"
        onCancel={onBack}
        onSave={onSuccess}
        saveKey="dictionary.close"
        skipValid
        spinnerWhenSubmitting
      />
    </>
  );
};

const ConsentFormWizard = ({ journeyId, onFormMaybeCompleted, opener }: Props) => {
  const intl = useIntl();
  const [documentFor, setDocumentFor] = useState<string | null>();
  const [medium, setMedium] = useState<SignedDocumentMediumEnum>();
  const [wizardStep, setWizardStep] = useState<WizardStep>(WizardStep.MEDIUM_SELECTION);

  const [createSignedDocumentAction] = useMutation<SignedDocumentCreateMutation, SignedDocumentCreateMutationVariables>(
    SignedDocumentCreateDocument,
    {
      update: (store, { data }) => {
        if (data?.signedDocumentCreate?.signedDocument) {
          const newSignedDocument = data.signedDocumentCreate.signedDocument;
          store.modify({
            fields: {
              signedDocuments(cachedSignedDocumentRefs: readonly Reference[]) {
                const newSignedDocumentRef = store.writeFragment({
                  data: newSignedDocument,
                  fragment: SignedDocumentFragmentDoc,
                  fragmentName: "SignedDocument"
                });
                if (newSignedDocumentRef) {
                  return [...cachedSignedDocumentRefs, newSignedDocumentRef];
                }
                return [...cachedSignedDocumentRefs];
              }
            },
            id: store.identify({ __typename: "Journey", id: journeyId })
          });
        }
      }
    }
  );

  const handleMediumSelection = useCallback((medium: SignedDocumentMediumEnum, documentFor?: string | null) => {
    setMedium(medium);
    setDocumentFor(documentFor);
    setWizardStep(WizardStep.SIGNATURE_SETTINGS);
  }, []);

  const handleCancel = useCallback(() => {
    opener.close();
    setMedium(undefined);
    setWizardStep(WizardStep.MEDIUM_SELECTION);
  }, [opener]);

  const handleBack = useCallback(() => {
    setWizardStep(WizardStep.MEDIUM_SELECTION);
  }, []);

  const handleComplete = useCallback(
    async (data?: SignedDocumentCreateData) => {
      if (medium && data?.documentFor) {
        return createSignedDocumentAction({
          variables: {
            documentFor: data.documentFor,
            journeyId,
            kind: SignedDocumentKindEnum.CONSENT_FORM,
            medium,
            recipientEmailAddress: data.recipientEmailAddress,
            recipientName: data.recipientName
          }
        }).then(response => {
          if (
            response.data?.signedDocumentCreate?.signedDocument?.embedUrl &&
            response.data?.signedDocumentCreate?.signedDocument?.medium === SignedDocumentMediumEnum.EMBED
          ) {
            if (isPlatform("desktop")) {
              const clientId = import.meta.env.VITE_HELLO_SIGN_CLIENT_ID;
              if (clientId) {
                const client = new HelloSign();
                client.open(response.data.signedDocumentCreate.signedDocument.embedUrl, {
                  clientId,
                  skipDomainVerification: true
                });
              }
            } else {
              const webUrl = new URL(import.meta.env.VITE_APP_HOST);
              webUrl.pathname = "embed-signed-document";
              webUrl.searchParams.append("embedUrl", response.data.signedDocumentCreate.signedDocument.embedUrl);
              const openCapacitorSite = async () => {
                await Browser.open({ url: webUrl.toString() });
                Browser.addListener("browserFinished", () => onFormMaybeCompleted());
              };
              openCapacitorSite();
            }
          }
          opener.close();
        });
      }
    },
    [createSignedDocumentAction, journeyId, medium, onFormMaybeCompleted, opener]
  );

  return (
    <Modal
      hideTitleBorder
      onIonModalDidDismiss={handleCancel}
      opener={opener}
      title={intl.formatMessage({ id: "pages.consentFormWizard.title" })}
    >
      {() => (
        <>
          {wizardStep === WizardStep.MEDIUM_SELECTION ? (
            <MediumSelectionForm onCancel={opener.close} onMediaSelection={handleMediumSelection} />
          ) : (
            <>
              {medium === SignedDocumentMediumEnum.EMAIL ? (
                <CompleteEmailForm onBack={handleBack} onSuccess={handleComplete} />
              ) : (
                <>
                  {medium === SignedDocumentMediumEnum.EMBED ? (
                    <CompleteEmbedForm documentFor={documentFor} onBack={handleBack} onSuccess={handleComplete} />
                  ) : (
                    <CompleteUpload onBack={handleBack} onSuccess={handleCancel} />
                  )}
                </>
              )}
            </>
          )}
        </>
      )}
    </Modal>
  );
};

export default ConsentFormWizard;
